import { booleanAttribute, ChangeDetectionStrategy, Component, effect, inject, input, numberAttribute, signal } from '@angular/core';

import { AvatarComponent } from '@shared/_views/avatar';

import { Chat, ChatStatus, ChatType, Guid, strings2Color } from '@models';
import { CacheService } from '@services/cache.service';
import { BaseMin } from '@base/base-min';

@Component({
    selector: 'vz-chat-avatar, [vz-chat-avatar]',
    template: `
        @if (chat(); as ch) {
            @if (ch.status == CS.Archived || ch.status == CS.Finished) {
                <vz-avatar [item]="$any({ initials: 'vzi-cabinet', color: 'var(--vz-textColorLL)', textColor: 'var(--vz-textColor)' })" withName="false" noTooltip="true" noBorder="true" [size]="size()" />
            }
            @if (ch.status != CS.Archived && ch.status != CS.Finished) {
                @switch (ch.type) {
                    @case (CT.Private) {
                        <vz-avatar [item]="$any({ initials: 'vzi-lock', color: 'var(--vz-textColorL)' })" withName="false" noTooltip="true" noBorder="true" [size]="size()" />
                    }
                    @case (CT.Public) {
                        <vz-avatar [item]="$any({ initials: '#', color: 'var(--vz-textColorL)' })" withName="false" noTooltip="true" noBorder="true" [size]="size()" />
                    }
                    @case (CT.Direct) {
                        @if (selfOnly()) {
                            <vz-avatar [item]="$any({ initials: 'vzi-bookmark2', color: 'var(--vz-primaryColorL)' })" withName="false" noTooltip="true" noBorder="true" [size]="size()" />
                        }
                        @else {
                            <vz-avatar [item]="avatar()" withName="false" noTooltip="true" noBorder="true" [size]="size()" [type]="group() ? 'group' : 'user'" />
                        }
                    }
                }
            }
            @if (withName()) {
                <span class="ml025 elli">{{ selfOnly() ? 'Избранное' : title() }}</span>
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AvatarComponent],
    host: {
        '[class.layout]': '!!withName()',
        '[class.center]': '!!withName()',
    }
})
export class ChatAvatarComponent extends BaseMin {
    readonly chatId     = input<Guid>();
    readonly withName   = input<boolean, unknown>(false, { transform: booleanAttribute });
    readonly size       = input<number, unknown>(18, { transform: numberAttribute });

    readonly chat       = signal<Chat | undefined>(undefined);
    readonly title      = signal<string>('');
    readonly selfOnly   = signal<boolean>(false);
    readonly group      = signal<boolean>(false);
    readonly avatar     = signal<any | undefined>(undefined);

    CT = ChatType;
    CS = ChatStatus;

    private readonly _cache     = inject(CacheService);

    private _1 = effect(() => {
        this.__U(['chat']);
        const chatId = this.chatId();
        if (chatId) {
            this._S.chat = this._cache.watch('chat', chatId).subscribe(st => this.chat.set(st.item));
        }
        else {
            this.chat.set(undefined);
        }
    });

    private _2 = effect(() => {
        const chat = this.chat();
        let title = '';
        let selfOnly = false;
        let group = false;
        if (chat) {
            const userId = this._userId();
            if (chat.type != ChatType.Direct) {
                title = chat.title;
            }
            else if (chat.thread.commentators.length == 1 && chat.thread.commentators[0]?.id == userId) {
                selfOnly = true;
            }
            else if (chat.thread.commentators.length) {
                const um = this._store?.getState('persons').items || {};
                const ids = chat.thread.commentators.filter(cm => cm.id != userId).map(cm => cm.id!);
                title = ids.map(id => um[id].name || '#' + id).join(', ');
                if (ids.length == 1) {
                    this.avatar.set(um[ids[0]]);
                }
                else if (ids.length == 2) {
                    this.avatar.set({ initials: um[ids[0]].name.substring(0, 1) + um[ids[1]].name.substring(0, 1), color: strings2Color(ids) });
                    group = true;
                }
                else {
                    this.avatar.set({ initials: ids.length > 99 ? '**' : '' + ids.length, color: strings2Color(ids) });
                    group = true;
                }
            }
        }
        this.title.set(title);
        this.selfOnly.set(selfOnly);
        this.group.set(group);
    });

}
